import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';

import {
  archiveWorkspaceDocument,
  createWorkspaceDocument,
  getArchivedWorkspaceDocuments,
  getWorkspaceDocuments,
  recoverWorkspaceDocument,
  updateWorkspaceDocument,
} from '../../../../../shared/api/magellan/workspaceDocument';
import type { NewWorkspaceDocument } from '../../../../../shared/entities/WorkspaceDocuments';
import {
  type WorkspaceDocument,
  WorkspaceDocumentType,
} from '../../../../../shared/entities/WorkspaceDocuments';

const getWorkspaceDocumentsQueryKey = [getWorkspaceDocuments.name];
const getArchivedWorkspaceDocumentsQueryKey = [
  getArchivedWorkspaceDocuments.name,
];

export function useWorkspaceDocumentsQuery() {
  return useSuspenseQuery({
    queryKey: getWorkspaceDocumentsQueryKey,
    queryFn: getWorkspaceDocuments,
  });
}

export function useArchivedWorkspaceDocumentsQuery() {
  return useSuspenseQuery({
    queryKey: getArchivedWorkspaceDocumentsQueryKey,
    queryFn: getArchivedWorkspaceDocuments,
  });
}

export function useUpdateWorkspaceDocumentQuery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateWorkspaceDocument,
    onMutate: async document => {
      await queryClient.cancelQueries({
        queryKey: getWorkspaceDocumentsQueryKey,
      });

      const previousDocuments = queryClient.getQueryData<WorkspaceDocument[]>(
        getWorkspaceDocumentsQueryKey,
      );

      queryClient.setQueryData(
        getWorkspaceDocumentsQueryKey,
        (old: WorkspaceDocument[]) =>
          old.map(d => (d.id === document.id ? document : d)),
      );

      return { previousDocuments };
    },
    onError: (_error, _document, context) => {
      if (context) {
        queryClient.setQueryData(
          getWorkspaceDocumentsQueryKey,
          context.previousDocuments,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getWorkspaceDocumentsQueryKey,
      });
    },
  });
}

export function useCreateWorkspaceDocumentQuery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createWorkspaceDocument,
    onMutate: async files => {
      await queryClient.cancelQueries({
        queryKey: getWorkspaceDocumentsQueryKey,
      });

      const newDocuments: NewWorkspaceDocument[] = files.map(
        ({ file, type }) => ({
          originalName: file.name,
          prettyName: file.name,
          type: type || WorkspaceDocumentType.UNKNOWN,
          S3FileURL: '',
          S3PreviewFileURL: '',
          workspaceId: 0,
          archivedAt: null,
        }),
      );

      queryClient.setQueryData(
        getWorkspaceDocumentsQueryKey,
        (old: WorkspaceDocument[]) => [...old, ...newDocuments],
      );

      const previousDocuments = queryClient.getQueryData<WorkspaceDocument[]>(
        getWorkspaceDocumentsQueryKey,
      );

      return { previousDocuments };
    },
    onError: (_error, _document, context) => {
      if (context) {
        queryClient.setQueryData(
          getWorkspaceDocumentsQueryKey,
          context.previousDocuments,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getWorkspaceDocumentsQueryKey,
      });
    },
  });
}

export function useArchiveWorkspaceDocumentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: archiveWorkspaceDocument,
    onMutate: async documentId => {
      await queryClient.cancelQueries({
        queryKey: getWorkspaceDocumentsQueryKey,
      });

      const oldData = queryClient.getQueryData<WorkspaceDocument[]>(
        getWorkspaceDocumentsQueryKey,
      );

      if (!oldData) {
        return false;
      }

      const newData: WorkspaceDocument[] = oldData.filter(
        document => document.id !== documentId,
      );

      queryClient.setQueryData(getWorkspaceDocumentsQueryKey, newData);

      return { oldData };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      queryClient.setQueryData(getWorkspaceDocumentsQueryKey, context.oldData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getWorkspaceDocumentsQueryKey,
      });
    },
  });
}

export function useRecoverWorkspaceDocumentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: recoverWorkspaceDocument,
    onMutate: async documentId => {
      await queryClient.cancelQueries({
        queryKey: getArchivedWorkspaceDocumentsQueryKey,
      });

      const oldData = queryClient.getQueryData<WorkspaceDocument[]>(
        getArchivedWorkspaceDocumentsQueryKey,
      );

      if (!oldData) {
        return false;
      }

      const newData: WorkspaceDocument[] = oldData.filter(
        document => document.id !== documentId,
      );

      queryClient.setQueryData(getArchivedWorkspaceDocumentsQueryKey, newData);

      return { oldData };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      queryClient.setQueryData(
        getArchivedWorkspaceDocumentsQueryKey,
        context.oldData,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getArchivedWorkspaceDocumentsQueryKey,
      });
    },
  });
}
